// A file the backend could not import reads the same everywhere: one neutral
// sentence, followed by the server's own explanation only where a screen asks for it
// (refusalSentence) and only when that explanation is showable. Which formats, vendors or library
// preparations are supported is not shown on screen (owner rule): anything the
// backend does not take simply fails that analysis's import.

export const IMPORT_FAILED_TEXT = 'This file could not be processed.'

// Refusal codes of an import (libra somatic_vendor_import / somatic_input_profiles).
export const IMPORT_REFUSAL_CODES = Object.freeze([
  'vendor_file_not_detected',
  'no_importer',
  'unverified_vendor_format',
])

// Server wording that names format support (importers, vendor exports). A
// reason like this is treated as a refusal even when it arrives without its code.
const FORMAT_SUPPORT_TEXT = /\bimporters?\b|\bexport to build on\b/i

export const isImportRefusal = (code, text) =>
  (typeof code === 'string' && IMPORT_REFUSAL_CODES.includes(code)) ||
  (typeof text === 'string' && FORMAT_SUPPORT_TEXT.test(text))

// The neutral sentence for an import refusal, else null (the caller shows its own text).
export const importRefusalText = (code, text) => (isImportRefusal(code, text) ? IMPORT_FAILED_TEXT : null)

// The neutral sentence without its full stop, for a reason set inside a sentence.
export const IMPORT_FAILED_CLAUSE = IMPORT_FAILED_TEXT.replace(/\.$/, '')

// A server reason as shown: the neutral sentence for an import refusal, else the
// reason itself (text first, then the code).
export const shownReason = (code, text) => importRefusalText(code, text) || text || code || null

// A served explanation that may be shown beside the neutral sentence: a sentence
// (it has a space), not a bare code, and not one that names format support or says
// something is not available.
const SHOWABLE_REASON = (text) =>
  typeof text === 'string' &&
  /\s/.test(text.trim()) &&
  !FORMAT_SUPPORT_TEXT.test(text) &&
  !/\bnot available\b|\byet\b/i.test(text)

// What a refused file or import reads: the neutral sentence, then the server's own
// explanation when it sent a showable one.
export const refusalSentence = (reasonText) => {
  const text = SHOWABLE_REASON(reasonText) ? reasonText.trim() : ''
  if (!text || text === IMPORT_FAILED_TEXT) return IMPORT_FAILED_TEXT
  return `${IMPORT_FAILED_TEXT} ${/[.!?]$/.test(text) ? text : `${text}.`}`
}
